// [목적] React Router v6 라우트 트리 — 인증 가드 + Wave 3 신규 라우트 + / 라우트 LandingPage/HomePage 분기
// [이유] WC-FE-AUTH-07 + FEED-01 — ProtectedRoute/GuestOnlyRoute 래핑 + /u/:handle + /profile redirect + /me/folios.
//         oauth-login Wave 5 WC-FE-ROUTES-01 흡수. Wave 3 FEED-01: /profile → ProfileRedirect(본인 handle redirect).
//         landing-page-restore — / 라우트 element 를 App → RootSwitch 로 교체 (guest=LandingPage / authenticated=App+HomePage).
//         hide-settings-email-row(2026-06-03): settings/email 라우트를 SHOW_EMAIL_ROW flag 로 게이팅.
//           SHOW_EMAIL_ROW=false 시 <Navigate to="/settings" replace /> 로 흡수 — 고아 라우트 차단 + replace 로 히스토리 루프 방지.
//         root-route-guest-onboarding-routing(2026-06-04): 충돌 해소 —
//           landing-page-restore(/ → RootSwitch, guest=LandingPage) 와 LGO-FE-02(session_expired 자동 redirect) 의
//           런타임 상충을 AuthProvider wasAuthenticatedRef latch 로 해소:
//           첫 게스트만 LandingPage, 실제 만료(wasAuthenticatedRef=true)만 /login redirect.
//           / index 를 ProtectedRoute(requireOnboarded) 로 래핑 — authenticated+!onboarded → /onboarding.
//         openui-oneshot-pdf-folio T8(2026-07-12): /new-folio 라우트 신규 추가 — 자연어 한 줄 → 1화면
//           생성형 UI 보완 폼(NewFolioPage). /chat(멀티턴 챗)과 병행 경로(D-2 — /chat 미변경), 공개 라우트.
//         M7 P2(발견 화면 필터+비공개 프로필): /u/:handle/followers · /u/:handle/following 라우트 신규 추가
//           (FollowListPage.tsx) — ProfilePage 팔로워/팔로잉 카운트 클릭 진입점. 비인증 허용(BE 와 동일 가드).
// [사이드 이팩트] /login, /onboarding → GuestOnlyRoute 래핑.
//                 /folio/:id/edit, /me/folios, /notifications, /settings/* → ProtectedRoute(requireOnboarded) 래핑.
//                 / index → ProtectedRoute(requireOnboarded) 래핑(2026-06-04 신규 — 온보딩 게이트 일관성).
//                 /auth/callback/:provider 는 GuestOnlyRoute 비적용 (loading 통과 필요).
//                 /profile → ProfileRedirect — 인증 사용자 /u/:handle 로 redirect, guest → /login
//                 / + status==='guest' → LandingPage (App 셸 미적용). 그 외 / children 은 RootSwitch 가 App 셸로 위임.
//                 settings/email: SHOW_EMAIL_ROW=false → /settings replace 리다이렉트. 인증 경계 약화 없음.
// [수정 시 고려사항] ProtectedRoute 의 loading Skeleton 이 OAuthCallbackPage 무한루프를 방지한다.
//                   라우트 추가 시 공개/보호 여부를 명시적으로 표시.
//                   RootSwitch 는 useAuthContext 의존 — AuthProvider 가 RouterProvider 상위에 있어야 한다 (main.tsx 보장).
//                   이메일 라우트 재노출 시 settings.flags.ts 의 SHOW_EMAIL_ROW 를 true 로 변경(단일 SoT).
//                   / index 의 ProtectedRoute(requireOnboarded): guest 는 RootSwitch 에서 LandingPage 로 분기(Outlet 미전개)
//                     → index 에 도달하지 않음 → ProtectedRoute guest→/login 분기 미트리거 → 이중처리 없음.

import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  type createBrowserRouter as CreateBrowserRouter,
} from 'react-router-dom';

import { ProtectedRoute } from './components/auth/ProtectedRoute.js';
import { GuestOnlyRoute } from './components/auth/GuestOnlyRoute.js';
import { SHOW_EMAIL_ROW } from './pages/settings/settings.flags.js';
import { RootSwitch } from './components/auth/RootSwitch.js';
import { LoginPage } from './pages/auth/LoginPage.js';
import { OnboardingPage } from './pages/auth/OnboardingPage.js';
import { OAuthCallbackPage } from './pages/auth/OAuthCallbackPage.js';
import { ChatPage } from './pages/ChatPage.js';
import { CollectionPage } from './pages/CollectionPage.js';
import { ExplorePage } from './pages/ExplorePage.js';
import { FestivalsPage } from './pages/FestivalsPage.js';
import { FestivalDetailPage } from './pages/FestivalDetailPage.js';
import { FolioEditPage } from './pages/FolioEditPage.js';
import { FolioPage } from './pages/FolioPage.js';
import { HomePage } from './pages/HomePage.js';
import { MyFoliosPage } from './pages/MyFoliosPage.js';
import { NotificationsPage } from './pages/NotificationsPage.js';
import { ProfilePage, ProfileRedirect } from './pages/ProfilePage.js';
import { FollowersPage, FollowingPage } from './pages/FollowListPage.js';
import { SettingsPage } from './pages/settings/SettingsPage.js';
import {
  DeleteAccountScreen,
  EmailScreen,
  ExportScreen,
  HelpScreen,
  PasswordScreen,
  PolicyScreen,
  ProfileEditScreen,
  TasteScreen,
} from './pages/settings/SettingsSubPages.js';

// [목적] NewFolioPage lazy import — /new-folio 라우트 전용 코드 스플리팅
// [이유] FE 강제 규칙(라우트 코드 스플리팅, React.lazy + Suspense). 기존 라우트들은 스플리팅 미적용
//        상태라 이번 신규 라우트 1건만 적용해도 전체 일관성을 해치지 않는다(다른 라우트 리팩터는 범위 밖).
// [사이드 이팩트] 최초 /new-folio 진입 시 별도 청크 네트워크 요청 1회 발생.
// [수정 시 고려사항] Suspense fallback 은 최소 placeholder — 로딩 스켈레톤 고도화는 후속 카드(T9 등)에서 협의.
const NewFolioPage = lazy(() =>
  import('./pages/NewFolioPage.js').then((m) => ({ default: m.NewFolioPage })),
);

/**
 * [목적] /new-folio 라우트 element — lazy 청크 로딩 중 Suspense fallback 표시
 * [이유] React.lazy 는 Suspense 경계 없이 사용 불가(React 규약) — 라우트 element 레벨에서 래핑.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] fallback 은 접근성 위해 role="status" 부여(스크린리더 로딩 안내).
 */
function NewFolioRoute(): JSX.Element {
  return (
    <Suspense fallback={<div role="status">로딩 중…</div>}>
      <NewFolioPage />
    </Suspense>
  );
}

const routes: RouteObject[] = [
  // OAuth callback — GuestOnlyRoute 비적용 (loading 상태에서 착지해야 함)
  { path: '/auth/callback/:provider', element: <OAuthCallbackPage /> },

  // 인증/온보딩 — GuestOnlyRoute 래핑 (인증 완료 사용자 차단)
  {
    path: '/login',
    element: (
      <GuestOnlyRoute>
        <LoginPage />
      </GuestOnlyRoute>
    ),
  },
  {
    path: '/onboarding',
    element: (
      // GuestOnlyRoute — authenticated + !isOnboarded 는 통과
      <GuestOnlyRoute>
        <OnboardingPage />
      </GuestOnlyRoute>
    ),
  },

  // 메인 셸 — / 는 RootSwitch 가 인증 상태 분기 (guest=LandingPage / authenticated=App+HomePage)
  {
    path: '/',
    element: <RootSwitch />,
    children: [
      // / index — authenticated+onboarded 만 HomePage 렌더.
      // !onboarded → ProtectedRoute(requireOnboarded) 가 /onboarding redirect.
      // guest → RootSwitch 가 LandingPage 렌더(Outlet 미전개) → 이 index 에 도달하지 않음.
      // (root-route-guest-onboarding-routing 2026-06-04: 충돌 해소 — 첫 게스트는 LandingPage,
      //  실제 만료만 /login redirect, 미온보딩은 /onboarding redirect.)
      {
        index: true,
        element: (
          <ProtectedRoute requireOnboarded>
            <HomePage />
          </ProtectedRoute>
        ),
      },
      { path: 'chat', element: <ChatPage /> },
      // openui-oneshot-pdf-folio T8: 자연어 한 줄 → 1화면 생성형 UI 보완 폼(신규 병행 경로, D-2 — /chat 미변경).
      // React.lazy + Suspense 코드 스플리팅(FE 강제 규칙) — 로그인 불필요(RootSwitch 는 비루트 경로 항상 App 셸 통과).
      { path: 'new-folio', element: <NewFolioRoute /> },
      { path: 'explore', element: <ExplorePage /> },
      // [수정: 디자인 개편] 강원 축제 전체 페이지 — /explore 와 동일하게 로그인 없이도 접근 가능(공개 정보).
      { path: 'festivals', element: <FestivalsPage /> },
      { path: 'festivals/:id', element: <FestivalDetailPage /> },
      { path: 'folio/:id', element: <FolioPage /> },
      { path: 'collections/:id', element: <CollectionPage /> },

      // 공개 프로필 — /u/:handle
      { path: 'u/:handle', element: <ProfilePage /> },
      // M7 P2: 팔로워/팔로잉 목록 — 비인증 허용(BE AnonOrAuthenticatedGuard), 비공개 프로필은 403(ErrorBoundary)
      { path: 'u/:handle/followers', element: <FollowersPage /> },
      { path: 'u/:handle/following', element: <FollowingPage /> },
      // 본인 프로필 진입 — 인증 사용자 /u/:handle 로 redirect, guest → /login?returnTo=/profile
      { path: 'profile', element: <ProfileRedirect /> },

      // 인증 필수 라우트 (requireOnboarded)
      {
        path: 'folio/:id/edit',
        element: (
          <ProtectedRoute requireOnboarded>
            <FolioEditPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'me/folios',
        element: (
          <ProtectedRoute requireOnboarded>
            <MyFoliosPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'notifications',
        element: (
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },

      // 설정 — ProtectedRoute 적용
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/profile',
        element: (
          <ProtectedRoute>
            <ProfileEditScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/email',
        element: SHOW_EMAIL_ROW ? (
          <ProtectedRoute>
            <EmailScreen />
          </ProtectedRoute>
        ) : (
          <Navigate to="/settings" replace />
        ),
      },
      {
        path: 'settings/password',
        element: (
          <ProtectedRoute>
            <PasswordScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/taste',
        element: (
          <ProtectedRoute>
            <TasteScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: 'settings/export',
        element: (
          <ProtectedRoute>
            <ExportScreen />
          </ProtectedRoute>
        ),
      },
      // 약관/도움말은 공개 접근 허용
      { path: 'settings/tos', element: <PolicyScreen kind="tos" /> },
      { path: 'settings/privacy', element: <PolicyScreen kind="privacy" /> },
      { path: 'settings/help', element: <HelpScreen /> },
      {
        path: 'settings/delete',
        element: (
          <ProtectedRoute>
            <DeleteAccountScreen />
          </ProtectedRoute>
        ),
      },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router: ReturnType<typeof CreateBrowserRouter> = createBrowserRouter(routes);
